// Play: your opening against a bot that plays it the way people at a level
// do. The bot plays into the opening, then plays what people at the chosen
// level play from there — in the book's proportions while the book lasts,
// and with a weakened engine after. Nothing is asked and nothing is marked:
// leaving the line is said, loudly, once, with the way back beside it.

import { useEffect, useRef, useState, type ReactNode } from "react";
import { errorText, getJson, isSignedOut, reportFault, sendJson } from "../app/api.ts";
import { persist, Superseded, type NetState } from "../app/net.ts";
import { bookInfo, chosenBand } from "../app/book.ts";
import { BANDS, cap, fmtCount, fmtScore, pct } from "../app/format.ts";
import { Icon } from "../app/icons.tsx";
import { prefs, savePrefs } from "../app/prefs.ts";
import { follow, goBack, navigate, setAddress } from "../app/router.ts";
import { toast } from "../app/toast.ts";
import { BackButton, type ScreenProps } from "../app/App.tsx";
import { Loading, Notice } from "../app/ui.tsx";
import { Board, type LastMove } from "../board/Board.tsx";
import { winChances } from "../board/scores.ts";
import type { Score } from "../board/lines.ts";
import type { HintLevel } from "../board/puzzle.ts";
import { BoardMenu, BoardScreen } from "./BoardScreen.tsx";
import { OrList } from "./opening-text.tsx";
import "./play.css";

/** The levels a bot can be asked to play at: the book's four, and masters. */
const LEVELS = [...BANDS, { value: "masters", label: "Masters" }];
const levelLabel = (value?: string) => LEVELS.find((l) => l.value === value)?.label || "your level";
const START = "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1";

interface Move {
  uci: string;
  san?: string;
  by?: "you" | "bot";
}

interface SparState {
  fen: string;
  fen_before_bot?: string;
  moves?: Move[];
  legal: string[];
  turn: "white" | "black";
  over?: string | null;
  winner?: string;
  level?: string;
  opening?: string;
  white_eval?: Score | null;
  eval_from?: { source: string; depth?: number; engine?: string } | null;
  line?: { status: "on" | "off" | "left"; ply: number; played_san: string; prepared?: { san: string; comment?: string }[] } | null;
  bot?: { san: string; source: "book" | "line" | "engine"; share?: number; read_at?: string; games?: number; unprepared?: boolean } | null;
  hint?: { uci: string; san: string; also_uci?: string[]; also?: string[]; source: string; comment?: string } | null;
}

interface Repertoire {
  id: number;
  name: string;
  color: "white" | "black";
}

type AudioCtx = typeof AudioContext;
let audio: AudioContext | null = null;

/** Two falling notes. Leaving the line is the one event in a practice game
 *  worth interrupting for, and a banner is easy not to look at. */
function alarmSound() {
  if (!prefs.sparSound) return;
  try {
    const Ctx = (window.AudioContext || (window as unknown as { webkitAudioContext?: AudioCtx }).webkitAudioContext) as AudioCtx | undefined;
    if (!Ctx) return;
    const ctx = (audio ||= new Ctx());
    if (ctx.state === "suspended") void ctx.resume();
    for (const [freq, at] of [
      [440, 0],
      [294, 0.16],
    ] as const) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "triangle";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, ctx.currentTime + at);
      gain.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + at + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + at + 0.22);
      osc.connect(gain).connect(ctx.destination);
      osc.start(ctx.currentTime + at);
      osc.stop(ctx.currentTime + at + 0.25);
    }
  } catch {
    // No sound: the strip still says it.
  }
  try {
    navigator.vibrate?.([120, 60, 120]);
  } catch {
    // No vibration either.
  }
}

const moveText = (ply: number, san: string) => `${Math.floor(ply / 2) + 1}${ply % 2 ? "…" : "."} ${san}`;
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export function PlayScreen({ params, query }: ScreenProps) {
  const id = params.id!;
  const [rep, setRep] = useState<Repertoire | null>(null);
  const [error, setError] = useState("");
  const [level, setLevel] = useState("");
  const meta = { title: "Play", nav: "openings", back: `/openings/${id}`, backLabel: "Repertoire" };

  useEffect(() => {
    (async () => {
      try {
        const [data, info] = await Promise.all([getJson(`/api/repertoires/${id}`), bookInfo().catch(() => ({ band: null }))]);
        setLevel(prefs.sparLevel || chosenBand() || info.band || "from1300");
        setRep(data.repertoire);
      } catch (err) {
        if (!isSignedOut(err)) setError(errorText(err));
      }
    })();
  }, [id]);

  return (
    <div className="screen spar-screen">
      <BackButton meta={meta} />
      {error ? (
        <Notice kind="error">{error}</Notice>
      ) : !rep ? (
        <Loading text="Setting up the board…" />
      ) : (
        <Game
          rep={rep}
          level={level}
          onLevel={(l) => {
            prefs.sparLevel = l;
            savePrefs();
            setLevel(l);
          }}
          // A game in progress is its moves, and the address carries them: a
          // reload in the middle of a game is the same game, not a new one.
          resumed={(query.get("moves") || "").split(",").filter((uci) => /^[a-h][1-8][a-h][1-8][qrbn]?$/.test(uci))}
          back={meta.back}
        />
      )}
    </div>
  );
}

function Game({ rep, level, onLevel, resumed, back }: { rep: Repertoire; level: string; onLevel: (l: string) => void; resumed: string[]; back: string }) {
  const [state, setState] = useState<SparState | null>(null);
  const [moves, setMoves] = useState<Move[]>([]);
  const [board, setBoard] = useState<{ fen: string; last: LastMove; legal: string[] }>({ fen: START, last: null, legal: [] });
  const [busy, setBusy] = useState(false);
  // How the request under way is going, and what it is for: a slow answer
  // and a dropped connection are said, not left to look like a frozen board.
  const [net, setNet] = useState<{ state: NetState; what: string }>({ state: { kind: "ok" }, what: "" });
  // Each request is the latest press's: a newer one — New game while a take
  // back waits on the connection — takes its place.
  const pressed = useRef(0);
  const [ended, setEnded] = useState<SparState | "ended" | null>(null);
  // The answer held back while the board is saying the line was left.
  const [held, setHeld] = useState<SparState | null>(null);
  const [hint, setHint] = useState<{ fen: string; level: HintLevel }>({ fen: "", level: "clear" });
  const [sound, setSound] = useState(Boolean(prefs.sparSound));
  // Where this game left the line and where the bot left the book, for the
  // summary: the first of each, since after that there is nothing to leave.
  const leftLine = useRef<{ ply: number; played: string; prepared: string[] } | null>(null);
  const botLeftBook = useRef<{ ply: number; san: string } | null>(null);
  const live = useRef({ state, moves, level });
  live.current = { state, moves, level };

  const open = (s: SparState) => !s.over && s.turn === rep.color;

  /** Ask the server for the position after `list`, and the bot's reply.
   *  `what` names the press for the strip while it waits: "your move". */
  async function sync(list: Move[], what = "your move") {
    const mine = ++pressed.current;
    setBusy(true);
    setNet({ state: { kind: "ok" }, what });
    try {
      const asked = Date.now();
      const data = await persist(
        (signal) => sendJson<SparState>("POST", "/api/spar", { repertoire: rep.id, color: rep.color, level: live.current.level, moves: list.map((m) => m.uci) }, { signal }),
        { onState: (state) => mine === pressed.current && setNet({ state, what }), superseded: () => mine !== pressed.current }
      );
      // First the move that was made, whole; then a beat; then the reply. A
      // reply that lands the instant the piece is put down reads as the board
      // malfunctioning rather than as somebody answering.
      const last = list[list.length - 1];
      if (data.bot && last && data.fen_before_bot) {
        setBoard({ fen: data.fen_before_bot, last: last.uci, legal: [] });
        await sleep(Math.max(250, 600 - (Date.now() - asked)));
        if (mine !== pressed.current) return;
      }
      if (data.line?.status === "off" && data.bot && !data.over) {
        // Off the line. The board stops on the move that did it, in red, and
        // the bot's answer waits until the person has decided between taking
        // it back and playing on.
        const before = (data.moves || []).slice(0, -1);
        const yours = before[before.length - 1];
        setHeld(data);
        setMoves(before);
        setBoard({ fen: data.fen_before_bot || data.fen, last: yours ? { bad: yours.uci } : null, legal: [] });
        noteLeft(data);
        alarmSound();
      } else {
        settle(data);
      }
    } catch (err) {
      // A newer press took this one's place: it has nothing more to say.
      if (err instanceof Superseded) return;
      if (!isSignedOut(err)) {
        // The server refused (a connection that fails is tried again, above):
        // the move stays off the record, and the board goes back to what the
        // server last agreed to.
        toast(errorText(err));
        const s = live.current.state;
        if (s) {
          const m = live.current.moves;
          setBoard({ fen: s.fen, last: m[m.length - 1]?.uci || null, legal: open(s) ? s.legal : [] });
        } else if (list.length) {
          // Moves out of an address that the server would not play: a new
          // game, rather than an empty board waiting for one.
          setTimeout(newGame, 0);
        }
      }
    } finally {
      if (mine === pressed.current) {
        setBusy(false);
        setNet({ state: { kind: "ok" }, what: "" });
      }
    }
  }

  function noteLeft(data: SparState) {
    const line = data.line;
    if (line && line.status === "off" && !leftLine.current) {
      leftLine.current = { ply: line.ply, played: line.played_san, prepared: (line.prepared || []).map((p) => p.san) };
    }
    if (data.bot?.source === "engine" && !botLeftBook.current) botLeftBook.current = { ply: (data.moves || []).length - 1, san: data.bot.san };
  }

  function settle(data: SparState) {
    const list = data.moves || [];
    setHeld(null);
    setState(data);
    setMoves(list);
    setAddress({ moves: list.map((m) => m.uci).join(",") });
    setBoard({ fen: data.fen, last: list[list.length - 1]?.uci || null, legal: open(data) ? data.legal : [] });
    noteLeft(data);
    if (data.over) setEnded(data);
  }

  function newGame() {
    setHeld(null);
    setEnded(null);
    leftLine.current = null;
    botLeftBook.current = null;
    setMoves([]);
    setState(null);
    setHint({ fen: "", level: "clear" });
    void sync([], "a new game");
  }

  useEffect(() => {
    if (resumed.length) void sync(resumed.map((uci) => ({ uci })));
    else newGame();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function takeBack() {
    // A press that does nothing is reported, with why: from a phone it is
    // otherwise only "Undo does not respond", with nothing to go on.
    const ignored = (why: string) =>
      reportFault("takeback-ignored", why, { source: "play", repertoire: rep.id, moves: moves.map((m) => `${m.uci}:${m.by || "?"}`).join(" "), held: Boolean(held), busy, ended: Boolean(ended) });
    if (busy || ended) return ignored(busy ? "busy" : "ended");
    setHeld(null);
    // Back to before the person's last move: their move, and the bot's
    // answer to it when there was one.
    let cut = moves.length;
    while (cut > 0 && moves[cut - 1]!.by !== "you") cut -= 1;
    if (cut === 0) return ignored("no move of yours to take back");
    cut -= 1;
    if (leftLine.current && leftLine.current.ply >= cut) leftLine.current = null;
    if (botLeftBook.current && botLeftBook.current.ply >= cut) botLeftBook.current = null;
    void sync(moves.slice(0, cut), "the take back");
  }

  /** The person has seen that they left the line, and goes on anyway. It
   *  was said once, loudly; played on, it is a fact about the game and not
   *  an alarm any more. */
  function playOn() {
    const data = held;
    if (!data || busy) return;
    settle({ ...data, line: data.line ? { ...data.line, status: "left" } : null });
  }

  // The move to play here, in three presses: the piece that moves, then the
  // move itself, then the hint taken away. A new position starts over.
  const hintNow = state && hint.fen === state.fen ? hint.level : "clear";
  function pressHint() {
    if (held) return playOn();
    const h = state?.hint;
    if (!h || busy || ended || !state) return;
    const next: HintLevel = hintNow === "clear" ? "square" : hintNow === "square" ? "arrow" : "clear";
    setHint({ fen: state.fen, level: next });
  }

  const yours = moves.some((m) => m.by === "you");
  const finished = ended !== null;
  const hintMoves = state?.hint ? [state.hint.uci, ...(state.hint.also_uci || [])] : [];
  const showing = held || state;
  const score = !showing || showing.over ? null : showing.white_eval || null;
  const evalFrom = score && showing?.eval_from;
  const fromText = !evalFrom
    ? ""
    : evalFrom.source === "book"
      ? `Score: Stockfish, depth ${evalFrom.depth || "?"}, from the book`
      : `Score: ${evalFrom.engine || "the engine"}, a short search`;
  // What a PGN of this game says it was: who played it, and how it ended.
  const botName = `Bot (${levelLabel(state?.level || level)})`;
  const over = ended && ended !== "ended" ? ended : null;
  const result = !over ? "*" : over.winner === "white" ? "1-0" : over.winner === "black" ? "0-1" : "1/2-1/2";

  const twoNames = (long: string, short: string) => (
    <>
      <span className="long">{long}</span>
      <span className="short">{short}</span>
    </>
  );

  return (
    <BoardScreen
      className="spar"
      board={
        <div className="board-zone">
          <Board
            className="play-board"
            fen={board.fen}
            orientation={rep.color}
            last={board.last}
            hint={state && hint.fen === state.fen && hintNow !== "clear" ? { level: hintNow, moves: hintMoves } : null}
            input={!busy && !finished && !held && board.legal.length ? { legal: board.legal, onMove: (uci) => void sync([...moves, { uci }]) } : null}
          />
          <div className="spar-bar" title="How the game stands: White's share of it, from the left">
            <div className="spar-bar-white" style={{ width: `${Math.round(((score ? winChances(score) : 0) + 1) * 50)}%` }} />
          </div>
        </div>
      }
      line={<Strip held={held} state={state} net={net} moves={moves} fromText={fromText} />}
      bar={
        <>
          <button type="button" className="btn menu-btn" onClick={() => goBack(back)}>
            <Icon name="back" />
            Back
          </button>
          <button type="button" className={"btn spar-back" + (held ? " btn-alarm" : "")} disabled={busy || !yours || finished} onClick={takeBack}>
            <Icon name="stepBack" />
            {twoNames("Take back", "Undo")}
          </button>
          <button
            type="button"
            className="btn hint-btn"
            title="What to play here: your line's move, else the book's, else the engine's"
            disabled={busy || finished || (!held && !state?.hint)}
            onClick={pressHint}
          >
            {held ? (
              <>
                <Icon name="next" />
                Play on
              </>
            ) : (
              <>
                <Icon name="eye" />
                {hintNow === "square" ? "Show" : hintNow === "arrow" ? "Hide" : "Hint"}
              </>
            )}
          </button>
          <button type="button" className="btn" disabled={busy && net.state.kind === "ok"} onClick={newGame}>
            <Icon name="skip" />
            {twoNames("New game", "New")}
          </button>
          <button type="button" className="btn btn-primary spar-end" disabled={busy || !yours || Boolean(held) || finished} onClick={() => setEnded("ended")}>
            {twoNames("End game", "End")}
          </button>
          <BoardMenu
            shown={() => ({
              fen: board.fen,
              start: START,
              moves: moves.map((m) => m.uci),
              headers: {
                Event: `Blundermine — ${rep.name}`,
                Site: location.href,
                White: rep.color === "white" ? "You" : botName,
                Black: rep.color === "white" ? botName : "You",
                Result: result,
              },
            })}
          />
        </>
      }
      sheet={
        <>
          <div className="puzzle-badges">
            <span className="badge opening">{cap(rep.color)}</span>
            <a {...follow(`/openings/${rep.id}`)}>{rep.name}</a>
          </div>
          {/* The opening the game is in, as far as it has got, and how the
              game stands, from White's side. */}
          <div className="spar-title-row">
            <h2 className="puzzle-title">{state?.opening || rep.name}</h2>
            <span className={"eval-pill " + (!score ? "none" : score.value < 0 ? "black" : "white")} title={score ? "How the game stands, from White's side" : "No evaluation yet"}>
              {score ? fmtScore(score) : "–"}
            </span>
          </div>
          {finished ? <Summary rep={rep} data={ended === "ended" ? null : ended} moves={moves} level={state?.level || level} leftLine={leftLine.current} botLeftBook={botLeftBook.current} /> : null}
          <Note held={held} state={state} moves={moves} hint={state && hint.fen === state.fen ? hintNow : "clear"} />
          <p className="puzzle-evals drill-path spar-path">
            {moves.length ? (
              moves.map((m, i) => (
                <span key={i}>
                  {i % 2 === 0 ? <span className="faint">{i / 2 + 1}. </span> : null}
                  <span className={m.by === "you" ? "spar-you" : "spar-bot"}>{m.san}</span>{" "}
                </span>
              ))
            ) : (
              <span className="faint">No moves yet.</span>
            )}
          </p>
          <div className="spar-level-row">
            <span className="label">The bot plays like</span>
            <div className="chips spar-levels">
              <button
                type="button"
                className="chip spar-sound"
                aria-pressed={sound}
                title="A sound when you leave your line"
                onClick={() => {
                  prefs.sparSound = !prefs.sparSound;
                  savePrefs();
                  setSound(prefs.sparSound);
                }}
              >
                {sound ? "Sound on" : "Sound off"}
              </button>
              {LEVELS.map((l) => (
                <button key={l.value} type="button" className="chip" aria-pressed={l.value === level} title={`The bot plays what ${l.label} players play`} onClick={() => onLevel(l.value)}>
                  {l.label}
                </button>
              ))}
            </div>
          </div>
        </>
      }
    />
  );
}

/** Under the board, one height whatever it says: what the bot just played
 *  and how common that is — and, in red, that the move just made was not
 *  the line's. */
function Strip({ held, state, net, moves, fromText }: { held: SparState | null; state: SparState | null; net: { state: NetState; what: string }; moves: Move[]; fromText: string }) {
  let body: ReactNode;
  const off = held?.line;
  const waiting = net.state.kind !== "ok";
  if (net.state.kind === "offline") {
    body = (
      <span className="spar-offline">
        <b>No connection.</b> Trying again to send {net.what}{net.state.attempt > 1 ? ` (attempt ${net.state.attempt + 1})` : ""} — it goes through as soon as the connection is back.
      </span>
    );
  } else if (net.state.kind === "slow") body = <span className="faint">Connecting… sending {net.what}.</span>;
  else if (off) {
    body = (
      <>
        <b>Off your line!</b> You prepared <OrList sans={(off.prepared || []).map((p) => p.san)} /> here, not <b>{off.played_san}</b>.
      </>
    );
  } else if (state?.bot) {
    const b = state.bot;
    body = (
      <>
        <b>{b.san}</b>
        {b.source === "book" ? ` — ${pct(b.share)} play this at ${b.read_at}` : b.source === "line" ? " — the way into your opening" : " — past the book: the engine"}
        {b.unprepared ? <span className="spar-new"> · not in your line: nothing prepared for it</span> : null}
        {fromText ? <span className="spar-from"> · {fromText}</span> : null}
      </>
    );
  } else body = <span className="faint">{moves.length ? "Your move." : "Your move. The bot plays into your opening first."}</span>;
  return (
    <div className={"spar-strip" + (off && !waiting ? " alarm" : "") + (net.state.kind === "offline" ? " offline" : "")} role="status" aria-live="assertive">
      {body}
    </div>
  );
}

/** What just happened, in a line or two, or the hint in words. */
function Note({ held, state, moves, hint }: { held: SparState | null; state: SparState | null; moves: Move[]; hint: HintLevel }) {
  const h = state?.hint;
  if (!held && h && hint !== "clear") {
    return (
      <div className="drill-note spar-note">
        {hint === "square" ? (
          <div className="drill-note-title">
            <span className="badge neutral">Hint</span> The marked piece moves. Press again for the move.
          </div>
        ) : (
          <>
            <div className="drill-note-title">
              <span className="badge neutral">Hint</span>
              {h.source === "line" ? " Your line plays " : h.source === "book" ? " The book recommends " : " The engine plays "}
              <b>{h.san}</b>
              {(h.also || []).length ? (
                <>
                  {" (or "}
                  <OrList sans={h.also!} />)
                </>
              ) : null}
              .
            </div>
            {h.comment ? <p>{h.comment}</p> : null}
          </>
        )}
      </div>
    );
  }
  const data = held || state;
  const line = data?.line;
  const bot = held ? null : data?.bot;
  const main = (line?.prepared || [])[0];
  return (
    <div className="drill-note spar-note">
      {line && line.status === "off" ? (
        <>
          <div className="drill-note-title">
            <span className="badge opening">Off your line</span> You prepared <OrList sans={(line.prepared || []).map((p) => p.san)} /> here, not <b>{line.played_san}</b>.
          </div>
          {main?.comment ? <p>{main.comment}</p> : null}
          <p className="drill-note-next">Play on, or take it back.</p>
        </>
      ) : null}
      {bot ? (
        <p className="spar-bot-note">
          <b>{bot.san}</b>
          {bot.source === "book"
            ? ` — ${pct(bot.share)} play this at ${bot.read_at} (${fmtCount(bot.games)} games).`
            : bot.source === "line"
              ? " — the way into your opening."
              : ` — past the book: the engine, weakened to ${levelLabel(data?.level)}.`}
        </p>
      ) : null}
      {!(line && line.status === "off") && !bot ? <span className="faint">{moves.length ? "Your move." : "Your move. The bot plays into your opening, then plays what people play."}</span> : null}
    </div>
  );
}

function Summary({
  rep,
  data,
  moves,
  level,
  leftLine,
  botLeftBook,
}: {
  rep: Repertoire;
  data: SparState | null;
  moves: Move[];
  level: string;
  leftLine: { ply: number; played: string; prepared: string[] } | null;
  botLeftBook: { ply: number; san: string } | null;
}) {
  const pgn = () => {
    const bot = `Blundermine bot (${levelLabel(level)})`;
    const tags = [
      ["Event", `Practice: ${rep.name}`],
      ["White", rep.color === "white" ? "You" : bot],
      ["Black", rep.color === "black" ? "You" : bot],
      ["Result", "*"],
    ];
    const body = moves.map((m, i) => (i % 2 === 0 ? `${i / 2 + 1}. ${m.san}` : m.san)).join(" ");
    return tags.map(([k, v]) => `[${k} "${v}"]`).join("\n") + `\n\n${body} *\n`;
  };
  return (
    <div className="drill-done spar-summary">
      <b>
        {data?.over === "checkmate"
          ? data.winner === rep.color
            ? "Checkmate — you won."
            : "Checkmate — the bot won."
          : data?.over
            ? `Drawn: ${data.over}.`
            : `Game ended after ${Math.ceil(moves.length / 2)} ${Math.ceil(moves.length / 2) === 1 ? "move" : "moves"}.`}
      </b>
      <p>
        {leftLine ? (
          <>
            You left your line at {moveText(leftLine.ply, leftLine.played)}
            {leftLine.prepared.length ? (
              <>
                {" — prepared: "}
                <OrList sans={leftLine.prepared} />
              </>
            ) : null}
            .
          </>
        ) : (
          "You stayed in your line for as long as it went."
        )}
      </p>
      {botLeftBook ? <p>The book ran out at {moveText(botLeftBook.ply, botLeftBook.san)}; from there the bot was the engine.</p> : null}
      <div className="btn-row">
        {moves.length >= 16 ? (
          <button
            type="button"
            className="btn btn-sm"
            title="The same analysis your Lichess games get: the tactics and blunders in it become puzzles"
            onClick={() => navigate(`/analyse?color=${rep.color}&input=${encodeURIComponent(pgn())}`)}
          >
            <Icon name="search" />
            Analyse this game
          </button>
        ) : null}
        {leftLine ? (
          <button type="button" className="btn btn-sm" onClick={() => navigate(`/openings/${rep.id}/drill`)}>
            <Icon name="target" />
            Drill the line
          </button>
        ) : null}
      </div>
    </div>
  );
}
